import { CheckIcon, LanguagesIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth/auth-provider';
import { PERMISSIONS } from '@/features/auth/permissions';
import { useContentLocale } from '@/features/locales/content-locale';
import { useLocales } from '@/features/locales/hooks';

/**
 * Topbar locale switcher — picks the language the browse screens read content
 * in. This used to be a read-only badge (every item was `disabled`) that always
 * showed the default locale, so there was no way to page through content in,
 * say, Turkish.
 */
export function LocaleIndicator() {
  const { can } = useAuth();
  const localesQuery = useLocales();
  const contentLocale = useContentLocale();

  if (!can(PERMISSIONS.locales.view)) {
    return null;
  }

  if (localesQuery.isPending) {
    return <Skeleton className="hidden h-8 w-24 sm:block" />;
  }

  if (localesQuery.isError || !localesQuery.data) {
    return null;
  }

  const locales = localesQuery.data;
  const active = locales.filter((locale) => locale.is_active);
  const selected = locales.find((locale) => locale.code === contentLocale.code);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5" aria-label="Content language">
          <LanguagesIcon aria-hidden="true" />
          <span className="hidden sm:inline">
            {selected?.code.toUpperCase() ?? '—'}
            <span className="text-muted-foreground"> · {active.length} active</span>
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <span className="block text-sm font-medium">Content language</span>
          <span className="block text-xs text-muted-foreground">
            Which translation the lists show
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {locales.map((locale) => {
          const isSelected = locale.code === contentLocale.code;

          return (
            <DropdownMenuItem
              key={locale.id}
              className="justify-between gap-2"
              disabled={!locale.is_active}
              onSelect={() => contentLocale.setCode(locale.code)}
            >
              <span className="flex min-w-0 items-center gap-2">
                <CheckIcon
                  aria-hidden="true"
                  className={isSelected ? 'size-4 shrink-0' : 'size-4 shrink-0 opacity-0'}
                />
                <span className="truncate">
                  <span className="font-medium uppercase">{locale.code}</span>
                  <span className="ml-2 text-muted-foreground">{locale.name}</span>
                </span>
              </span>
              <span className="flex shrink-0 gap-1">
                {locale.is_default ? <Badge variant="secondary">default</Badge> : null}
                {locale.is_active ? null : <Badge variant="secondary">off</Badge>}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
